import { answerText } from '@fieldops/shared'
import { useEffect, useState } from 'react'
import { DialogHeader, Modal } from '../Modal.js'
import { Button } from '../ui.js'
import type { AssistSource, ReportAnswers, ReportSuggestion } from './types.js'

/**
 * The suggestions, offered.
 *
 * This sheet is the whole of decision 55. Every assist in the product arrives
 * here, ticked, and one tap rejects it — nothing writes itself into a field.
 * That is not a preference about interaction design: a report is a document
 * somebody signs, and an answer that appeared without being chosen is an answer
 * the signer cannot be said to have made.
 *
 * Three groups, in order of how much they know. An answer recorded at this
 * address is worth more than the same person's habit at other addresses, and a
 * sentence composed from answers on this very report is worth less than either —
 * it is a starting point for wording, not a claim about the property. Each row
 * says where it came from in words, so a technician can weigh it rather than
 * trust it.
 */

/** The answer this suggestion would overwrite, or null if the field is blank. */
function replaced(suggestion: ReportSuggestion, answers: ReportAnswers): string | null {
  const current = answerText(answers[suggestion.fieldKey])
  if (current.length === 0) return null
  return current === answerText(suggestion.value) ? null : current
}

const GROUPS: Array<{ source: AssistSource; title: string; blurb: string }> = [
  {
    source: 'heard',
    title: 'From what you just said',
    blurb: 'Matched on this phone, word by word. Read each one — it is not a mind reader.',
  },
  {
    source: 'carry',
    title: 'From the last report at this address',
    blurb: 'Only the answers that do not change between visits. Findings are never carried.',
  },
  {
    source: 'usual',
    title: 'How you usually answer',
    blurb: 'Your own recent reports, not anybody else’s.',
  },
  {
    source: 'draft',
    title: 'Written from your answers above',
    blurb: 'A starting point for the wording. Read it before you keep it.',
  },
]

export function AssistSheet({
  open,
  onClose,
  suggestions,
  answers,
  loading,
  onApply,
}: {
  open: boolean
  onClose: () => void
  suggestions: ReportSuggestion[]
  /** What is on the report now, so a row can say what it would replace. */
  answers: ReportAnswers
  loading: boolean
  onApply: (accepted: ReportSuggestion[]) => void
}) {
  // Ticked on open, with one exception: a suggestion that would overwrite an
  // answer already on the report arrives unticked. Carry-forward and habit never
  // do — they are filtered to blank fields — but dictation can, because somebody
  // reading a section aloud says things they have already typed, and losing a
  // typed answer to a misheard word is the one failure here that is expensive.
  const [rejected, setRejected] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (!open) return
    setRejected(
      new Set(
        suggestions
          .filter((suggestion) => replaced(suggestion, answers) !== null)
          .map((suggestion) => suggestion.fieldKey),
      ),
    )
    // Only on open: re-running as `answers` changes would re-tick a row the
    // technician has just decided about.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const accepted = suggestions.filter((suggestion) => !rejected.has(suggestion.fieldKey))

  return (
    <Modal open={open} onClose={onClose} labelledBy="assist-sheet-title" className="max-w-[520px]">
      <DialogHeader
        id="assist-sheet-title"
        title="Suggestions"
        description="Nothing here is on the report yet. Untick anything you do not want."
        onClose={onClose}
      />

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
        {loading ? (
          <p className="text-[13px] text-ink-500">Looking at what was written here before…</p>
        ) : suggestions.length === 0 ? (
          <div className="rounded-lg bg-ink-50 px-3 py-5 text-center ring-1 ring-ink-200 ring-inset">
            <p className="text-sm font-semibold text-ink-900">Nothing to suggest.</p>
            <p className="mt-1 text-[13px] text-ink-600">
              No earlier report at this address, no habit worth claiming, and nothing to draft from
              yet. Fill in a few answers and look again.
            </p>
          </div>
        ) : null}

        {GROUPS.map(({ source, title, blurb }) => {
          const rows = suggestions.filter((suggestion) => suggestion.source === source)
          if (rows.length === 0) return null

          return (
            <section key={source} className="flex flex-col gap-2">
              <div className="flex flex-col gap-0.5">
                <h3 className="text-[13px] font-semibold text-ink-900">{title}</h3>
                <p className="text-xs text-ink-500">{blurb}</p>
              </div>

              {rows.map((suggestion) => {
                const on = !rejected.has(suggestion.fieldKey)

                return (
                  <button
                    key={suggestion.fieldKey}
                    type="button"
                    role="checkbox"
                    aria-checked={on}
                    onClick={() =>
                      setRejected((current) => {
                        const next = new Set(current)
                        if (on) next.add(suggestion.fieldKey)
                        else next.delete(suggestion.fieldKey)
                        return next
                      })
                    }
                    className="flex items-start gap-3 rounded-lg bg-white px-3 py-2.5 text-left ring-1 ring-ink-200 ring-inset transition-colors hover:bg-ink-50"
                  >
                    <span
                      aria-hidden
                      className={
                        on
                          ? 'mt-0.5 grid size-[18px] shrink-0 place-items-center rounded-[5px] bg-ink-900 text-[11px] font-bold text-white'
                          : 'mt-0.5 size-[18px] shrink-0 rounded-[5px] bg-white ring-1 ring-ink-300 ring-inset'
                      }
                    >
                      {on ? '✓' : ''}
                    </span>
                    <span className="flex min-w-0 flex-col gap-0.5">
                      <span className="text-[11px] font-semibold tracking-[0.03em] text-ink-500 uppercase">
                        {suggestion.label}
                      </span>
                      <span className="text-[13.5px] leading-relaxed text-ink-900">
                        {suggestion.text}
                      </span>
                      <span className="text-xs text-ink-500">{suggestion.reason}</span>
                      {replaced(suggestion, answers) === null ? null : (
                        <span className="text-xs font-medium text-ink-800">
                          Replaces “{replaced(suggestion, answers)}”, so it is not ticked.
                        </span>
                      )}
                    </span>
                  </button>
                )
              })}
            </section>
          )
        })}
      </div>

      <div className="flex items-center gap-2.5 border-t border-ink-200 px-4 py-3">
        <Button onClick={onClose}>Not now</Button>
        <Button
          variant="primary"
          size="lg"
          block
          disabled={accepted.length === 0}
          onClick={() => {
            onApply(accepted)
            onClose()
          }}
        >
          {accepted.length === 0
            ? 'Nothing ticked'
            : accepted.length === 1
              ? 'Put this one on the report'
              : `Put these ${accepted.length} on the report`}
        </Button>
      </div>
    </Modal>
  )
}
